import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Machine } from "../GameComponents/Machine";
import { CustomUtils } from "../Utils/CustomUtils";
import { ReelExtended } from "./ReelExtended";
import { WinAnimatorExtended } from "./WinAnimatorExtended";
import { PlayList } from "../Components/Sounds/PlayList";
import { ModuleManager } from "../Managers/ModuleManager";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";
import { DataManager } from "../Managers/DataManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { Symbol } from "../GameComponents/Symbol";
import { AppContext } from "../../Common/AppContext";
import { Symbols } from "../GameComponents/Symbols";

/**
 * Machine handle reels and symbols for reels rotation.
 * Machine define behaviour for complete flow of reel rotation.
 * @class Machine
 * @extends Machine
 */
@engineComponent(ComponentTypes.DisplayObject)
export class MachineExtended extends Machine {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    static NudgeMachineStopped: string = "nudgeMachineStopped";
    static NudgeReelStopped: string = "onNudgeReelStopped";
    static NudgeStopped: string = "onNudgeStopped";

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    public static NudgeReelIndexes: Array<number> = [];

    private nudgeReelStopCount: number;

    private _background: SwitchElement;
    private _teasing: SwitchElement;
    private spinBitmask: number;

    private _canTease2: boolean;
    private _canTease3: boolean;
    private _canTease4: boolean;

    constructor() {
        super();

        this.spinBitmask = 0;
    }

    public initialize(
        symbols: Symbols,
        reels: Array<Array<number>>,
        teasing?: SwitchElement,
        background?: SwitchElement,
    ): void {
        this._teasing = teasing;
        this._background = background;

        super.initialize(symbols, reels);
    }

    //******************************************************************************************************************
    //  NUDGE SPIN
    //******************************************************************************************************************

    nudgeSpin(): void {
        this.nudgeReelStopCount = 0;

        for (let i = 0; i < MachineExtended.NudgeReelIndexes.length; i++) {
            this._reels[MachineExtended.NudgeReelIndexes[i]].nudgeSpin();
        }
    }

    /**
     * Start spinning reels.
     * @method spinStart
     * @param longRotation boolean
     * @param useTransform
     * @param currentSymbols
     * @return void
     */
    public spinStart(
        longRotation?: boolean,
        useTransform: boolean = false,
        currentSymbols?: Array<Array<Symbol>>,
        spinBitmask: number = 0,
    ): void {
        this.spinBitmask = spinBitmask;
        if (this.spinBitmask !== 0) {
            if (DEBUG) {
                this.log("spinStart");
            }

            ReelExtended.Tease = false;
            WinAnimatorExtended.Force = false;

            this._stoppedFromServer = false;
            this._stoppedFromMachine = false;
            this._stoppedFromLegal = false;

            this._startSpinTime = Date.now();

            this.visible = true;
            this._reelsContainer.visible = true;

            this.actualReelStopped = -1;
            this._stoppedReelIndexes = 0;
            this._spinnedReelsCounter = 0;
            this._stoppingReelsCounter = 0;

            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if ((spinBitmask & (1 << i)) !== 0) {
                            if (this._stoppedSymbolsArray.length > 0) {
                                this._reels[i].visible = true;
                                this._reels[i].setData(this._stoppedSymbolsArray[i]);

                                //move current symbols from WinAnimator to Reel, not array reference
                                this._reels[i].currentSymbols = [];
                                for (let j = 0; j < this._visibleSymbolCount; j++) {
                                    this._reels[i].currentSymbols.push(currentSymbols[i][j]);
                                    currentSymbols[i][j] = null;
                                }

                                this._reels[i].showSymbols(useTransform);
                                this._reels[i].spinStart();
                                this._spinnedReelsCounter++;
                            } else {
                                if (DEBUG) {
                                    this.log("ERROR no stopped symbols");
                                }
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }

                if (longRotation != null && longRotation) {
                    this._stopTimer.start(this._minSpinRotationLong);
                } else {
                    this._stopTimer.start(this._minSpinRotation);
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }

            if (ModuleManager.freespinsActive()) {
                PlayList.play("SND_BaseRotationStart");
            } else {
                PlayList.play("SND_BaseRotationStart");
            }

            PlayList.loop("SND_BaseRotation", 0, true);
        }
    }

    spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (this.spinBitmask !== 0) {
            if (fromServer) {
                this._stoppedFromServer = true;
                if (this._legalStopTimer != null) {
                    clearTimeout(this._legalStopTimer);
                }
                if (this.verifyMinGameRoundDurationConditions()) {
                    this._legalStopTimer = setTimeout(
                        () => this.spinStop(null, false, false, true),
                        this.getLegalRemainingSpinDuration(),
                    );
                } else {
                    this._stoppedFromLegal = true;
                }
            } else if (legal) {
                if (this._legalStopTimer != null) {
                    clearTimeout(this._legalStopTimer);
                }
                this._stoppedFromLegal = true;
            } else {
                this._stopTimer.stop();
                this._stoppedFromMachine = true;
            }

            if (force) {
                WinAnimatorExtended.Force = true;
                PlayList.stop("SND_BaseFSTeaser");

                if (this._legalStopTimer != null) {
                    clearTimeout(this._legalStopTimer);
                }
                this._stoppedFromLegal = true;
                this._stopTimer.stop();
                this._stoppedFromMachine = true;
            }

            if (DEBUG) {
                this.log("spinStop: " + force);
            }

            if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
                if (
                    !ModuleManager.freespinsActive() &&
                    !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")
                ) {
                    DataManager.updateDataElement(GameData.DataTotalWin, "0");
                    DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
                }
                if (force && this._teasing != null) {
                    this._teasing.goToCase("EMPTY");
                    if (this._background != null) {
                        this._background.goToCase("BASE");
                    }
                }

                this._canTease2 = force ? false : this.checkTeasing(2);
                this._canTease3 = force ? false : this.checkTeasing(3);
                this._canTease4 = force ? false : this.checkTeasing(4);

                if (this._reels != null) {
                    for (let i: number = 0; i < this._reelCount; i++) {
                        if (this._reels[i] != null) {
                            if ((this.spinBitmask & (1 << i)) !== 0) {
                                // Stop only spinned reels.
                                if (this._stoppedSymbolsArray.length !== 0) {
                                    this._stoppingReelsCounter++;
                                    this._reels[i].setData(this._stoppedSymbolsArray[i]);

                                    if (force || (!this._canTease2 && !this._canTease3 && !this._canTease4)) {
                                        this._reels[i].spinStop(force);
                                    } else {
                                        if (this._canTease2 && this._canTease3 && this._canTease4) {
                                            if (i === 0 || i === 1) {
                                                this._reels[i].spinStop(force);
                                            }
                                        } else if (this._canTease3 && this._canTease4) {
                                            if (i === 0 || i === 1 || i === 2) {
                                                this._reels[i].spinStop(force);
                                            }
                                        } else if (this._canTease4) {
                                            if (i === 0 || i === 1 || i === 2 || i === 3) {
                                                this._reels[i].spinStop(force);
                                            }
                                        }
                                    }
                                } else {
                                    if (DEBUG) {
                                        this.log("ERROR no stopped symbols");
                                    }
                                }
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no reel with index: " + i);
                            }
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reels");
                    }
                }
            }
        } else {
            this.emit(Machine.MachineStopped);
        }

        if (fromServer) {
            MachineExtended.NudgeReelIndexes = CustomUtils.getNudgeReelIndexes();
        }
    }

    spinStopFS(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (this.spinBitmask !== 0) {
            if (fromServer) {
                this._stoppedFromServer = true;
                if (this._legalStopTimer != null) {
                    clearTimeout(this._legalStopTimer);
                }
                if (this.verifyMinGameRoundDurationConditions()) {
                    this._legalStopTimer = setTimeout(
                        () => this.spinStop(null, false, false, true),
                        this.getLegalRemainingSpinDuration(),
                    );
                } else {
                    this._stoppedFromLegal = true;
                }
            } else if (legal) {
                if (this._legalStopTimer != null) {
                    clearTimeout(this._legalStopTimer);
                }
                this._stoppedFromLegal = true;
            } else {
                this._stopTimer.stop();
                this._stoppedFromMachine = true;
            }

            if (force) {
                WinAnimatorExtended.Force = true;
                PlayList.stop("SND_BaseFSTeaser");

                if (this._legalStopTimer != null) {
                    clearTimeout(this._legalStopTimer);
                }
                this._stoppedFromLegal = true;
                this._stopTimer.stop();
                this._stoppedFromMachine = true;
            }

            if (DEBUG) {
                this.log("spinStop: " + force);
            }

            if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
                if (
                    !ModuleManager.freespinsActive() &&
                    !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")
                ) {
                    DataManager.updateDataElement(GameData.DataTotalWin, "0");
                    DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
                }
                if (force && this._teasing != null) {
                    this._teasing.goToCase("EMPTY");
                    if (this._background != null) {
                        this._background.goToCase("BASE");
                    }
                }

                this._canTease2 = force ? false : this.checkTeasing(2);
                this._canTease3 = force ? false : this.checkTeasing(3);
                this._canTease4 = force ? false : this.checkTeasing(4);

                if (this._reels != null) {
                    for (let i: number = 0; i < this._reelCount; i++) {
                        if (this._reels[i] != null) {
                            if ((this.spinBitmask & (1 << i)) !== 0) {
                                // Stop only spinned reels.
                                if (this._stoppedSymbolsArray.length !== 0) {
                                    this._stoppingReelsCounter++;
                                    this._reels[i].setData(this._stoppedSymbolsArray[i]);

                                    if (force || (!this._canTease2 && !this._canTease3 && !this._canTease4)) {
                                        this._reels[i].spinStop(force);
                                    } else {
                                        if (this._canTease2 && this._canTease3 && this._canTease4) {
                                            if (i === 0 || i === 1) {
                                                this._reels[i].spinStop(force);
                                            }
                                        } else if (this._canTease3 && this._canTease4) {
                                            if (i === 0 || i === 1 || i === 2) {
                                                this._reels[i].spinStop(force);
                                            }
                                        } else if (this._canTease4) {
                                            if (i === 0 || i === 1 || i === 2 || i === 3) {
                                                this._reels[i].spinStop(force);
                                            }
                                        }
                                    }
                                } else {
                                    if (DEBUG) {
                                        this.log("ERROR no stopped symbols");
                                    }
                                }
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no reel with index: " + i);
                            }
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reels");
                    }
                }
            }
        } else {
            this.emit(Machine.MachineStopped);
        }

        if (fromServer) {
            MachineExtended.NudgeReelIndexes = CustomUtils.getNudgeReelIndexesFS();
        }
    }

    public onNudgeReelStopped(reelIndex: number): void {
        this.nudgeReelStopCount++;
        this._reels[reelIndex].visible = false;
        this.emit(MachineExtended.NudgeReelStopped, reelIndex);

        if (this.nudgeReelStopCount == MachineExtended.NudgeReelIndexes.length) {
            this.emit(MachineExtended.NudgeMachineStopped);
        }
    }

    //******************************************************************************************************************
    //  OVERRIDES
    //******************************************************************************************************************

    protected createReels(reels: Array<Array<number>>): void {
        super.createReels(reels);

        for (let i = 0; i < this._reelCount; i++) {
            const reel = this._reels[i];
            reel.on(ReelExtended.NudgeReelStopped, this.onNudgeReelStopped.bind(this));
        }
    }

    public onReelStopped(reelIndex: number): void {
        const nudgeReel = MachineExtended.NudgeReelIndexes.indexOf(reelIndex) > -1;

        let scattersCount = 0;
        if (this._teasing != null) {
            for (let i = 0; i <= reelIndex; i++) {
                if (this._stoppedSymbolsArray[i].symbols.indexOf(CustomUtils.SymbolScatter) !== -1) {
                    scattersCount++;
                }
            }

            this.tease(reelIndex);
        }

        PlayList.play("SND_BaseRotationEnd" + reelIndex);

        this._stoppedReelIndexes++;
        this.actualReelStopped = reelIndex;

        if (!nudgeReel) {
            this._reels[reelIndex].visible = false;
            this.emit(Machine.ReelStopped);

            this.hideNonTeasableSymbols(scattersCount, reelIndex);
        } else {
            //todo preverit
            if (ReelExtended.Tease) {
                this.emit(MachineExtended.NudgeStopped);

                for (let symbolIndex = 0; symbolIndex < this._reels[reelIndex].currentSymbols.length; symbolIndex++) {
                    let symbol = this._reels[reelIndex].currentSymbols[symbolIndex];
                    if (!(CustomUtils.NudgeSymbols.indexOf(symbol.id + 1) > -1)) {
                        symbol.goToCase(Symbol.CaseNowin);
                    }
                }
            }
        }

        if (this._stoppedReelIndexes === this._spinnedReelsCounter) {
            PlayList.stop("SND_BaseRotation", 10);
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stopTimer.stop();
            if (DEBUG) {
                const old = AppContext.gameConfiguration.MinGameRoundDuration;
                const current = AppContext.gameConfiguration.GameRoundMinSpan;
                const minDuration = current > 0 ? current : old > 0 ? old : 0;
                const spinDuration = Date.now() - this._startSpinTime;

                if (spinDuration > minDuration || !this.verifyMinGameRoundDurationConditions()) {
                    this.log("Spin time duration: " + spinDuration + " ms");
                } else {
                    console.log("%cError spin time duration:" + spinDuration + " ms", "color: #ff9900");
                }
            }

            this.emit(Machine.MachineStopped);

            this._reelsContainer.visible = false;
            if (this._teasing != null && this._teasing.getCurrentCase() !== "EMPTY") {
                this._teasing.goToCase("END");

                if (this._background != null) {
                    this._background.goToCase("BASE");
                }
            }
        }

        if (MachineExtended.NudgeReelIndexes.length > 0) {
            this._reelsContainer.visible = true;
        }
    }

    private checkTeasing(reelIndex: number): boolean {
        if (this._teasing == null) {
            return false;
        }

        let count = 0;

        for (let i = 0; i < reelIndex; i++) {
            if (this._stoppedSymbolsArray[i].symbols.indexOf(CustomUtils.SymbolScatter) !== -1) {
                count++;
            }
        }

        return count >= 2;
    }

    private tease(reelIndex: number): void {
        switch (reelIndex) {
            case 1:
                if (this._canTease2) {
                    ReelExtended.Tease = true;
                    this._reels[2].spinTease([CustomUtils.SymbolScatter], 130);

                    PlayList.play("SND_BaseFSTeaser");

                    if (this._teasing != null) {
                        this._teasing.goToCase("TEASING_START_3");
                    }
                    if (this._background != null) {
                        this._background.goToCase("TEASE");
                    }
                }

                break;
            case 2:
                if (this._canTease3) {
                    ReelExtended.Tease = true;
                    this._reels[3].spinTease([CustomUtils.SymbolScatter], 150);

                    if (!this._canTease2) {
                        PlayList.play("SND_BaseFSTeaser");
                    }

                    if (this._teasing != null && this._teasing.getCurrentCase() === "EMPTY") {
                        this._teasing.goToCase("TEASING_START_4");
                    }
                    if (this._background != null) {
                        this._background.goToCase("TEASE");
                    }
                }

                break;
            case 3:
                if (this._canTease4) {
                    ReelExtended.Tease = true;
                    this._reels[4].spinTease([CustomUtils.SymbolScatter], 100);

                    if (!this._canTease3) {
                        PlayList.play("SND_BaseFSTeaser");
                    }

                    if (this._teasing != null && this._teasing.getCurrentCase() === "EMPTY") {
                        this._teasing.goToCase("TEASING_START_5");
                    }
                    if (this._background != null) {
                        this._background.goToCase("TEASE");
                    }
                }

                break;
            case 4:
                //if (this._canTease4) {
                PlayList.stop("SND_BaseFSTeaser");

                let count = 0;
                for (let i = 0; i <= reelIndex; i++) {
                    if (this._stoppedSymbolsArray[i].symbols.indexOf(CustomUtils.SymbolScatter) !== -1) {
                        count++;
                    }
                }

                if (count >= 3) {
                    PlayList.play("SND_BaseFSTeaserWin");
                } else {
                    let count2 = 0;
                    for (let i = 0; i < reelIndex; i++) {
                        if (this._stoppedSymbolsArray[i].symbols.indexOf(CustomUtils.SymbolScatter) !== -1) {
                            count2++;
                        }
                    }

                    if (count2 == 2) {
                        PlayList.play("SND_BaseFSTeaserFail");
                    }
                }
                //}

                break;
        }
    }

    private hideNonTeasableSymbols(scattersCount: number, reelIndex: number): void {
        if (scattersCount >= 2) {
            for (let i = 0; i < reelIndex; i++) {
                for (let symbolIndex = 0; symbolIndex < this._reels[i].currentSymbols.length; symbolIndex++) {
                    if (this._reels[i].currentSymbols[symbolIndex] != null) {
                        if (this._reels[i].currentSymbols[symbolIndex].id === CustomUtils.SymbolScatter - 1) {
                            this._reels[i].currentSymbols[symbolIndex].goToCase(Symbol.CaseTease);
                        } else if (this._reels[i].currentSymbols[symbolIndex].getCurrentCase() == Symbol.CaseEmpty) {
                            //nothing
                        } else {
                            if (!WinAnimatorExtended.Force) {
                                this._reels[i].currentSymbols[symbolIndex].goToCase(Symbol.CaseNowin);
                            }
                        }
                    }
                }
            }
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineExtended.ts
 **/